"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Minus, Plus } from "lucide-react";
import { Chip, ChipRow } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { INR_PER_USD, formatMoney, useCurrency } from "@/lib/currency";
import { useSignedIn } from "@/lib/auth/auth-hint";
import { cn } from "@/utils/ui";

const QUICK = [100, 200, 300, 500, 1000];

/**
 * Editable top-up: type any amount in steps of ₹100 (or tap a chip / use +/-),
 * see the credits update, then pay. One component for the public pages
 * (links to sign-in) and the credits page (opens checkout).
 * Credits use the server's rule: round(rupees × creditsPerRupee).
 */
export function TopUpPicker({
	creditsPerRupee,
	min = 100,
	max = 5000,
	step = 100,
	initial = 300,
	onPay,
	payHref = "/login",
	payLabel,
	busy,
	disabled,
	disabledReason,
	className,
}: {
	creditsPerRupee: number;
	min?: number;
	max?: number;
	step?: number;
	initial?: number;
	onPay?: (amountInr: number, credits: number) => void;
	payHref?: string;
	payLabel?: string;
	busy?: boolean;
	disabled?: boolean;
	disabledReason?: string;
	className?: string;
}) {
	const currency = useCurrency() ?? "INR";
	const user = useSignedIn();
	const formatInr = (rupees: number) => formatMoney(rupees, currency);
	// The field is typed in the visitor's own currency; everything else (limits, credits, payment) stays in rupees.
	const unit = currency === "USD" ? INR_PER_USD : 1;
	const [text, setText] = useState(String(initial));
	const shownUnit = useRef(1);
	useEffect(() => {
		if (shownUnit.current !== unit) {   // the currency became known after the first paint: show the same amount in it
			const was = shownUnit.current;   // read now: React may run the update below later, after the ref has moved on
			shownUnit.current = unit;
			setText((t) => String(Math.round((Number(t.replace(/[^0-9]/g, "")) * was) / unit)));
		}
	}, [unit]);
	const n = Number(text.replace(/[^0-9]/g, "")) * unit;
	const valid = text !== "" && n >= min && n <= max && n % step === 0;
	const credits = valid ? Math.round(n * creditsPerRupee) : 0;
	const set = (v: number) => setText(String(Math.min(max, Math.max(min, v)) / unit));

	let hint = "";
	if (!valid) hint = text === "" ? `Enter an amount from ${formatInr(min)} to ${formatInr(max)}.` : `Use a multiple of ${formatInr(step)} between ${formatInr(min)} and ${formatInr(max)}.`;

	// Public pages: a signed-in person is sent to the credits page with the amount; a visitor is sent to sign in.
	const label = payLabel ?? (onPay ? `Pay ${valid ? formatInr(n) : ""}`.trim() : user ? `Top up ${valid ? formatInr(n) : ""}`.trim() : "Sign in to top up");
	const href = !onPay && user ? `/credits?topup=${valid ? n : ""}` : payHref;
	const payProps = { className: "min-h-11 w-full sm:min-h-9 sm:w-auto", "data-testid": "topup-pay" } as const;

	return (
		<div data-testid="topup-picker" className={cn("flex flex-col gap-4", className)}>
			<ChipRow>
				{QUICK.filter((a) => a >= min && a <= max).map((a) => (
					<Chip key={a} selected={valid && n === a} onClick={() => set(a)} data-testid={`topup-${a}`}>
						{formatInr(a)}
					</Chip>
				))}
			</ChipRow>

			<div className="flex flex-wrap items-end gap-x-6 gap-y-3">
				<div>
					<label htmlFor="topup-amount" className="mb-1.5 block text-sm text-muted-foreground">Amount</label>
					<div className="flex items-center gap-1">
						<Button type="button" variant="outline" size="icon" aria-label={`Decrease by ${formatInr(step)}`} className="size-11 sm:size-9" disabled={!valid || n <= min} onClick={() => set(n - step)}>
							<Minus />
						</Button>
						<div className="relative">
							<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden>{currency === "USD" ? "$" : "₹"}</span>
							<input
								id="topup-amount"
								data-testid="topup-input"
								inputMode="numeric"
								autoComplete="off"
								value={text}
								onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
								aria-invalid={!valid}
								aria-describedby="topup-hint"
								className={cn(
									"h-11 w-32 rounded-md border bg-input pl-7 pr-3 text-base tabular-nums text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary/30 sm:h-9",
									valid ? "border-border-strong focus-visible:border-primary" : "border-destructive",
								)}
							/>
						</div>
						<Button type="button" variant="outline" size="icon" aria-label={`Increase by ${formatInr(step)}`} className="size-11 sm:size-9" disabled={!valid || n >= max} onClick={() => set(n + step)}>
							<Plus />
						</Button>
					</div>
				</div>
				<p className="pb-1.5 text-sm">
					<span data-testid="topup-credits" className="font-mono text-lg font-semibold tabular-nums text-primary">{credits.toLocaleString("en-IN")}</span>{" "}
					<span className="text-muted-foreground">credits</span>
				</p>
				<div className="w-full sm:ml-auto sm:w-auto">
					{onPay ? (
						<Button {...payProps} disabled={!valid || busy || disabled} title={disabled ? disabledReason : undefined} onClick={() => valid && onPay(n, credits)}>
							{busy ? "Opening…" : disabled && disabledReason ? disabledReason : label}
						</Button>
					) : (
						<Button asChild {...payProps}>
							<Link href={href}>{label}</Link>
						</Button>
					)}
				</div>
			</div>
			<p id="topup-hint" role={valid ? undefined : "alert"} className={cn("min-h-5 text-sm", valid ? "text-muted-foreground" : "text-destructive")}>
				{valid ? "Packs give more credits for the same money." : hint}
				{valid && currency === "USD" && <span data-testid="topup-charge-note"> You are charged ₹{n.toLocaleString("en-IN")} at checkout ({formatInr(n)} at ₹{INR_PER_USD} = $1).</span>}
			</p>
		</div>
	);
}
